/**
 * Reading a Micronaut `Page` off the wire.
 *
 * <h2>🪤 The page count and the page number are NOT sent</h2>
 *
 * A Micronaut `Page` serialises as `{ content, pageable: { number, size }, totalSize }`.
 * `totalPages` and `pageNumber` are computed getters, and Serde does not write them —
 * so `payload.totalPages ?? 1` reads 1 for every page, every pager decides it is on its
 * only page, and the next button sits disabled at "1–20 of 973".
 *
 * <p>That was found twice: on the jobs board, fixed there, and then again on the quotes
 * board a day later, because the reading was written out twice. It is written once here
 * so a third list cannot repeat it.
 *
 * <p>Also tolerates a bare array (an older server, or a stubbed one) rather than
 * white-screening the board that renders it.
 *
 * @param {object|Array} payload what the thunk received
 * @param {{ size: number, page?: number }} asked the page that was REQUESTED — the
 *        fallback when the response says nothing, since it is what was actually served
 * @returns {{ content: Array, total: number, page: number, totalPages: number }}
 */
export function readPage(payload, { size, page = 0 } = {}) {
    const body = payload ?? {};

    if (Array.isArray(body)) {
        return { content: body, total: body.length, page: 0, totalPages: 1 };
    }

    const content = body.content ?? [];
    const total = body.totalSize ?? body.totalElements ?? content.length;
    const pageSize = body.pageable?.size ?? body.size ?? size ?? content.length ?? 1;

    return {
        content,
        total,
        page: body.pageable?.number ?? body.pageNumber ?? body.number ?? page,
        // Derived, because the server does not send it. Never below 1: a lane with
        // nothing in it is still on its one and only page.
        totalPages: body.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
    };
}

export default readPage;
